import {
  ArrowsRightLeftIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ListBulletIcon,
  PhotoIcon,
  RectangleGroupIcon,
  TableCellsIcon,
  UserCircleIcon,
  UserGroup,
} from 'components'
import { useContext } from 'react'
import {
  Action,
  CommonLayoutProps,
  ILayout,
  LayoutItem,
  LayoutType,
  MultiLanguageString,
  TemplateDocs,
} from 'types'
import { SessionContext, useExplorerConfig } from '../components/providers'
import { LayoutHelpers } from '../desktopTypes'
import { useResultTraitGroupsWithSelections } from './resultUtils'
import { ScaleProps } from './scaleUtils'
import { getFilledTraitMap } from './traitUtils'

export function useContent(id: string) {
  const { templateDocs } = useExplorerConfig()
  return templateDocs?.content.find(c => c.id === id)
}

export function useLayoutMap() {
  const { templateDocs } = useExplorerConfig()
  return { ...templateDocs?.template.layoutMap, ...layoutOverrides }
}

export function useLayout(key?: number) {
  const layoutMap = useLayoutMap()
  if (!key) return

  return layoutMap[key]
}

export function getLayouts(layoutMap?: { [key: number]: ILayout }) {
  if (!layoutMap) return []
  return Object.values({ ...layoutMap, ...layoutOverrides })
}

/** Override any layout for testing purposes */
export const layoutOverrides: { [key: number]: ILayout } = {}

export function useParseText() {
  const { templateDocs } = useExplorerConfig()
  const { getLanguageValue } = useContext(SessionContext)
  const { content } = templateDocs || {}

  function parser(text?: MultiLanguageString) {
    if (!text) return ''

    const _text = getLanguageValue(text)

    return _text.replace(/\{\{(.+?)\}\}/g, (_, textInsert) => {
      const [type, id] = textInsert.split('|')
      if (type === 'content') {
        const value = content?.find(c => c.id === id)?.value
        if (value) return getLanguageValue(value)
      }
      return ''
    })
  }

  return parser
}

/** convert LayoutItems into Layouts (flattening TraitGroup generated layouts) */
export function useLayoutItems(
  layoutItems: (LayoutItem & CommonLayoutProps)[],
  rootLayoutKeyPath?: string
): (LayoutItem & Partial<ILayout> & LayoutHelpers)[] {
  const { templateDocs } = useExplorerConfig()
  const { getLanguageValue } = useContext(SessionContext)
  const layoutMap = useLayoutMap()
  const selectedTraits = useResultTraitGroupsWithSelections()

  if (!templateDocs) return []

  const traitGroupMap = getFilledTraitMap(templateDocs)
  let items: (Partial<ILayout> & LayoutHelpers)[] = []

  layoutItems.forEach(layout => {
    if (layout.traitGroupKey != null) {
      const group = traitGroupMap[layout.traitGroupKey]
      const groupsWithSelections = selectedTraits.find(t => t.title.en === group.title.en)
      if (!groupsWithSelections) return

      const selectedValues = groupsWithSelections.selected.map((s, i) => {
        const value = group.values.find(v =>
          s.traitId ? s.traitId === v.id : getLanguageValue(v.title) === s.title
        )
        return { ...value, valueIndex: i }
      })

      items = items.concat(
        selectedValues.map((t): ILayout & LayoutHelpers => ({
          ...layout,
          type: 'trait',
          keyPath: `${rootLayoutKeyPath}/${layout.key}`,
          style: {
            ...layout.style,
            background: t?.media ? { mediaId: t.media } : { color: 'panel' },
          },
          traitKey: layout.traitGroupKey!,
          valueIndex: t.valueIndex!,
          ...(t?.title && { title: { text: t.title, icon: t.icon, iconFill: 'primary' } }),
        }))
      )
    } else if (layout.key != null) {
      const { key, requiredTrait } = layout
      if (requiredTrait) {
        const hasTrait = requiredTrait.some(r =>
          selectedTraits?.some(
            s => s.step.key === r.key && s.values?.some((_, i) => i === r.valueIndex)
          )
        )
        if (!hasTrait) return
      }
      const layoutItem = layoutMap[key]
      if (layoutItem)
        items.push({
          keyPath: `${rootLayoutKeyPath}/${key}`,
          ...layoutItem,
          ...layout,
          style: { ...layout.style, ...layoutItem.style },
        })
    } else {
      console.warn('Invalid layout item:', layout)
    }
  })

  return items
}

export function getDefaultActionDimensions(action?: Action, templateDocs?: TemplateDocs | null) {
  if (!templateDocs || !action) return { height: 800, width: 1200 }

  const actionLayout = templateDocs?.template.layoutMap?.[action!.layoutKey]
  let height = actionLayout?.style?.height
  let width = actionLayout?.style?.width
  if (height && width) return { height, width }

  switch (actionLayout?.type) {
    case 'media':
      const mediaId = actionLayout.mediaId
      const media = templateDocs?.media.find(m => m.id === mediaId)
      if (!media) break

      if (media.type === 'pdf') return { height: defaultDialogHeight, width: defaultDialogWidth }

      if (media.dimensions) {
        const ratio = media.dimensions.width / media.dimensions.height
        if (typeof height === 'number') {
          width = height * ratio
        } else if (typeof width === 'number') {
          height = width / ratio
        } else {
          height = Math.min(defaultDialogHeight, media.dimensions.height)
          width = height * ratio
          if (width > defaultDialogWidth) {
            width = defaultDialogWidth
            height = width / ratio
          }
        }
      }
      break

    case 'tool':
      height = height || defaultDialogHeight
      width = width || defaultDialogWidth
  }

  return { height, width }
}

const defaultDialogHeight = 800
const defaultDialogWidth = 1200

export function parsePadding(
  layout?: Pick<ILayout, 'style' | 'title'>,
  isTitle?: boolean
): ScaleProps {
  if (!layout?.style?.padding?.length) return {}

  const p = layout.style.padding
  const hasTitle = !!layout.title?.text

  if (!p?.length) return { p: 0 }

  const titleCorrection = hasTitle ? (isTitle ? { pb: 0 } : { pt: 0 }) : undefined

  switch (p.length) {
    case 1:
      return { p: p[0], ...titleCorrection }
    case 2:
      return { py: p[0], px: p[1], ...titleCorrection }
    case 3:
      return { pt: p[0], px: p[1], pb: p[2], ...titleCorrection }
    default:
      return {
        pt: p[0],
        pr: p[1],
        pb: p[2],
        pl: p[3],
        ...titleCorrection,
      }
  }
}

export const keyFromPath = (keyPath: string) => Number(keyPath.split('/').pop())

export const iconByLayoutType: Record<LayoutType, React.ComponentType<any>> = {
  carousel: ArrowsRightLeftIcon,
  flex: RectangleGroupIcon,
  greeting: ChatBubbleOvalLeftEllipsisIcon,
  grid: TableCellsIcon,
  media: PhotoIcon,
  'selected-traits': UserGroup,
  tabs: ListBulletIcon,
  text: DocumentTextIcon,
  tool: GlobeAltIcon,
  trait: UserCircleIcon,
}
